import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Checkbox,
  Separator,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { ChevronDown, Check } from 'lucide-react';
import {
  type EnabledTabs,
  DEFAULT_ENABLED_TABS,
} from '../constants/intakeTabs';
import { isIncludeAllSelection } from '../utils/intake';

export type StatusFilterType =
  | 'active'
  | 'include_all'
  /** @deprecated Persisted legacy; treated as active */
  | 'show_all'
  | 'completed'
  | 'non_admit'
  | 'archived'
  | 'incomplete_extraction'
  | 'incomplete_eligibility'
  | 'incomplete_prior_auth'
  | 'incomplete_ready_to_call'
  | 'incomplete_scheduling'
  | 'incomplete_push_to_emr'
  // `pending_step_` and not `pending_`: the backend's IntakeStatus enum already
  // owns pending_review / pending_clarification / pending_eligibility /
  // pending_prior_auth, so a bare `pending_` prefix would collide with them on
  // the wire.
  | 'pending_step_extraction'
  | 'pending_step_eligibility'
  | 'pending_step_prior_auth'
  | 'pending_step_ready_to_call'
  | 'pending_step_scheduling'
  | 'pending_step_push_to_emr';

interface IntakeStatusFilterProps {
  value: StatusFilterType[];
  onChange: (value: StatusFilterType[]) => void;
  enabledTabs?: EnabledTabs;
}

/**
 * How a checked step is interpreted.
 *
 * - `incomplete`: the step is not complete/skipped, whatever came before it.
 * - `pending`: the step is the intake's *current* step -- the amber dot in
 *   `StepStatusIndicator` (every earlier step done, this one not).
 */
type StepMode = 'incomplete' | 'pending';

const STEP_OPTIONS: { tabKey: string; label: string }[] = [
  { tabKey: 'extraction', label: 'Extraction' },
  { tabKey: 'eligibility', label: 'Eligibility' },
  { tabKey: 'prior_auth', label: 'Prior Auth' },
  { tabKey: 'ready_to_call', label: 'Ready to Call' },
  { tabKey: 'scheduling', label: 'Scheduling' },
  { tabKey: 'push_to_emr', label: 'Push to EMR' },
];

const MODE_LABEL: Record<StepMode, string> = {
  incomplete: 'Incomplete',
  pending: 'Pending',
};

const MODE_CAPTION: Record<StepMode, string> = {
  incomplete: 'Any step not done yet',
  pending: 'Current step (orange dot)',
};

const STEP_MODE_PREFIX: Record<StepMode, string> = {
  incomplete: 'incomplete_',
  pending: 'pending_step_',
};

const stepFilterKey = (mode: StepMode, tabKey: string) =>
  `${STEP_MODE_PREFIX[mode]}${tabKey}` as StatusFilterType;

const isPendingStepFilter = (filter: StatusFilterType) =>
  filter.startsWith(STEP_MODE_PREFIX.pending);

const isStepFilter = (filter: StatusFilterType) =>
  filter.startsWith(STEP_MODE_PREFIX.incomplete) || isPendingStepFilter(filter);

const stepFilterTabKey = (filter: StatusFilterType) =>
  isPendingStepFilter(filter)
    ? filter.slice(STEP_MODE_PREFIX.pending.length)
    : filter.slice(STEP_MODE_PREFIX.incomplete.length);

const isActiveSelection = (value: StatusFilterType[]) =>
  value.length === 0 ||
  (value.length === 1 && (value[0] === 'active' || value[0] === 'show_all'));

const IntakeStatusFilter: React.FC<IntakeStatusFilterProps> = ({
  value,
  onChange,
  enabledTabs = DEFAULT_ENABLED_TABS,
}) => {
  const [open, setOpen] = React.useState(false);

  // Filter step options based on enabled tabs
  const visibleStepOptions = STEP_OPTIONS.filter(opt => {
    if (opt.tabKey === 'extraction') return true; // Always show extraction
    return enabledTabs[opt.tabKey as keyof typeof enabledTabs] !== false;
  });

  const isActive = isActiveSelection(value);
  const isIncludeAll = isIncludeAllSelection(value);
  const isCompleted = value.includes('completed');
  const isNonAdmit = value.includes('non_admit');
  const isArchived = value.includes('archived');
  const stepFilters = value.filter(isStepFilter);

  // The mode is derived from the selection (so persisted `incomplete_*` arrays
  // restore into Incomplete mode with no migration). The local override only
  // decides which segment is highlighted while nothing is checked, and is kept
  // in sync with the derived mode so unchecking the last step (or a Reset)
  // can't silently flip Pending back to Incomplete.
  const [modeOverride, setModeOverride] =
    React.useState<StepMode>('incomplete');
  const derivedStepMode: StepMode | null =
    stepFilters.length > 0
      ? stepFilters.some(isPendingStepFilter)
        ? 'pending'
        : 'incomplete'
      : null;
  const stepMode: StepMode = derivedStepMode ?? modeOverride;

  React.useEffect(() => {
    if (derivedStepMode) setModeOverride(derivedStepMode);
  }, [derivedStepMode]);

  const handleActive = () => {
    onChange(['active']);
    setOpen(false);
  };

  const handleIncludeAll = () => {
    onChange(['include_all']);
    setOpen(false);
  };

  const handleCompleted = () => {
    onChange(['completed']);
    setOpen(false);
  };

  const handleNonAdmit = () => {
    onChange(['non_admit']);
    setOpen(false);
  };

  const handleArchived = () => {
    onChange(['archived']);
    setOpen(false);
  };

  // Switching the mode never changes which boxes are checked -- it remaps the
  // current selection in place (incomplete_prior_auth <-> pending_prior_auth)
  // so results update without the user re-checking anything.
  const handleModeChange = (nextMode: StepMode) => {
    if (nextMode === stepMode) return;
    setModeOverride(nextMode);
    if (stepFilters.length === 0) return;
    onChange(
      stepFilters.map(filter =>
        stepFilterKey(nextMode, stepFilterTabKey(filter))
      )
    );
  };

  const handleStepToggle = (tabKey: string) => {
    const key = stepFilterKey(stepMode, tabKey);

    // If currently in a mutually exclusive state, switch to step-filter mode
    if (isActive || isIncludeAll || isCompleted || isNonAdmit || isArchived) {
      onChange([key]);
      return;
    }

    // Toggle the step filter
    if (stepFilters.includes(key)) {
      const newFilters = stepFilters.filter(f => f !== key);
      onChange(newFilters.length === 0 ? ['active'] : newFilters);
    } else {
      onChange([...stepFilters, key]);
    }
  };

  // Get display text for the button
  const getDisplayText = () => {
    if (isIncludeAll) return 'Show All';
    if (isCompleted) return 'Completed';
    if (isNonAdmit) return 'Non-Admit';
    if (isArchived) return 'Archived';
    if (stepFilters.length === 1) {
      const opt = STEP_OPTIONS.find(
        o => o.tabKey === stepFilterTabKey(stepFilters[0])
      );
      return opt ? `${MODE_LABEL[stepMode]}: ${opt.label}` : 'Filtered';
    }
    if (stepFilters.length > 1) {
      return `${stepFilters.length} ${MODE_LABEL[stepMode]} Steps`;
    }
    return 'Active';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          // Fill the toolbar column (same pattern as IntakeAssigneeFilter).
          className='h-8 w-full justify-between min-w-[180px] font-normal'
        >
          <span className='truncate'>{getDisplayText()}</span>
          <ChevronDown className='h-4 w-4 opacity-50 ml-2 flex-shrink-0' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='start'>
        <div className='p-1'>
          {/* Active — excludes archived and non-admit (default list) */}
          <button
            onClick={handleActive}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isActive && <Check className='h-4 w-4 text-primary' />}
            {!isActive && <div className='w-4' />}
            Active
          </button>

          <Separator className='my-1' />

          {/* Show All — active + completed + archived + non-admit */}
          <button
            onClick={handleIncludeAll}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isIncludeAll ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isIncludeAll && <Check className='h-4 w-4 text-primary' />}
            {!isIncludeAll && <div className='w-4' />}
            Show All
          </button>

          <Separator className='my-1' />

          {/* Completed */}
          <button
            onClick={handleCompleted}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isCompleted ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isCompleted && <Check className='h-4 w-4 text-primary' />}
            {!isCompleted && <div className='w-4' />}
            Completed
          </button>

          <Separator className='my-1' />

          {/* Non-Admit */}
          <button
            onClick={handleNonAdmit}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isNonAdmit ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isNonAdmit && <Check className='h-4 w-4 text-primary' />}
            {!isNonAdmit && <div className='w-4' />}
            Non-Admit
          </button>

          <Separator className='my-1' />

          {/* Archived */}
          <button
            onClick={handleArchived}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isArchived ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isArchived && <Check className='h-4 w-4 text-primary' />}
            {!isArchived && <div className='w-4' />}
            Archived
          </button>

          <Separator className='my-1' />

          {/* Steps (multi-select), read as Incomplete or Pending */}
          <div className='py-1'>
            <div className='px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider'>
              Steps
            </div>
            <div className='px-3 pb-1'>
              <Tabs
                value={stepMode}
                onValueChange={nextMode =>
                  handleModeChange(nextMode as StepMode)
                }
              >
                <TabsList className='grid grid-cols-2 w-full'>
                  <TabsTrigger value='incomplete'>Incomplete</TabsTrigger>
                  <TabsTrigger value='pending'>Pending</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className='pt-1 text-[10px] text-muted-foreground leading-tight'>
                {MODE_CAPTION[stepMode]}
              </div>
            </div>
            {visibleStepOptions.map(opt => {
              const isChecked = stepFilters.includes(
                stepFilterKey(stepMode, opt.tabKey)
              );
              return (
                <button
                  key={opt.tabKey}
                  onClick={() => handleStepToggle(opt.tabKey)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
                    hover:bg-muted transition-colors text-left
                    ${isChecked ? 'text-foreground' : 'text-muted-foreground'}
                  `}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => handleStepToggle(opt.tabKey)}
                    className='pointer-events-none'
                  />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default IntakeStatusFilter;
